import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { IneligibilityService } from 'src/app/modules/iep/iep-document/services/ineligibility-service';
import { SelectedStaff } from 'src/app/modules/pwn/interfaces/source-staff-payload';
import { StaffContact } from 'src/app/modules/referrals/interfaces/staff-contact';
import { StaffSearchService } from 'src/app/services/staff-search.service';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { DropdownOption } from '../../interfaces/dropdown-option';
import { StaffInfo } from '../../interfaces/staff-info';
import {
  validateAlphaNumeric,
  validateEmail,
  validateName,
} from '../../validators/validator';

@Component({
    selector: 'app-staff-search',
    templateUrl: './staff-search.component.html',
    styleUrls: ['./staff-search.component.scss'],
    standalone: false
})
export class StaffSearchComponent implements AfterViewInit, OnDestroy {
  gridData: StaffInfo[];
  selectedStaff: SelectedStaff;
  disableSearch: boolean = false;
  @Input() showAddress: boolean;
  @Input() showSchool: boolean;
  @Input() showEmail: boolean = true;
  @Input() showSelectedStaffInfo: boolean = true;
  @Input() isAssistanceContact: boolean;
  @Input() readOnly: boolean = false;
  @Output() onSelectedStaff: EventEmitter<SelectedStaff> =
    new EventEmitter<StaffContact>();
  @Output() formValues: EventEmitter<StaffInfo> = new EventEmitter<StaffInfo>();
  @Output() selectedStaffFormStatus: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  loadingPanelVisible: boolean = false;
  states: DropdownOption[];
  private destroySubject = new Subject<void>();
  selectedStaffForm!: FormGroup;
  showSearchError: boolean = false;
  isSearchVisible: boolean = true;
  @Output() onResetSearch: EventEmitter<boolean> = new EventEmitter<boolean>();
  readonly select: string = 'Select';
  readonly reselect: string = 'Reselect';

  constructor(
    public fb: FormBuilder,
    private staffService: StaffSearchService,
    private referenceStore: ReferenceStore,
    private ineligibilityService: IneligibilityService,
    private router: Router
  ) {
    this.#createRtiForm();
    this.referenceStore.state$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((res: DropdownOption[]) => {
        this.states = res;
      });
    // Reset search error when the user types in any field
    this.staffForm.valueChanges.subscribe(() => {
      this.showSearchError = false;
    });
  }

  staffForm: FormGroup = this.fb.group({
    firstName: ['', validateName(false)],
    lastName: ['', validateName(false)],
    loginId: [''],
  });

  #createRtiForm(): void {
    this.selectedStaffForm = this.fb.group({
      fullName: [''],
      school: [''],
      streetAddress1: [''],
      streetAddress2: [''],
      city: [''],
      state: [null],
      zip: [''],
      workPhone: [''],
      phone: [''],
      email: [''],
      title: [''],
    });
  }

  onStaffSearch(): void {
    this.showSearchError =
      !this.staffForm.value.firstName &&
      !this.staffForm.value.lastName &&
      !this.staffForm.value.loginId;
    if (this.showSearchError) {
      this.staffForm.setErrors({ incorrect: true });
      return;
    } else {
      this.loadingPanelVisible = true;

      this.staffService.getStaffData(this.staffForm.value).subscribe({
        next: (res: StaffInfo[]) => {
          this.gridData = res;
          this.loadingPanelVisible = false;
          if (this.gridData.length) {
            this.gridData.map((staff: StaffInfo) => {
              staff.fullName = `${staff.firstName} ${staff.lastName}`;
              staff.phone = staff.workPhone; // Ensure phone property is set
            });
          }
        },
        error: () => {
          this.loadingPanelVisible = false;
        },
      });
    }
    this.selectedStaffForm.statusChanges.subscribe(() => {
      this.selectedStaffFormStatus.emit(this.selectedStaffForm.valid);
    });
  }

  onselectStaff(index: number): void {
    this.selectedStaff = this.gridData[index];
    this.patchSelectedStaffToForm();
    this.formValues.emit(this.staffForm.value);

    this.gridData = undefined;

    if (this.isAssistanceContact) {
      this.ineligibilityService.getSelectedStaffData(this.selectedStaff, true);
    } else {
      this.ineligibilityService.getSelectedStaffData(this.selectedStaff, false);
    }
    this.setValidators();
    this.onSelectedStaff.next(this.selectedStaff);
    this.isSearchVisible = true;
  }

  patchSelectedStaffToForm(): void {
    const EMAIL_CONTROL: AbstractControl = this.selectedStaffForm.get('email');
    EMAIL_CONTROL.addValidators([Validators.required]);
    EMAIL_CONTROL.updateValueAndValidity();

    this.selectedStaffForm.patchValue({
      fullName: this.selectedStaff?.fullName || '',
      school:
        this.selectedStaff?.locationName || this.selectedStaff?.school || '',
      streetAddress1: this.fullAddress?.trim() || '',
      streetAddress2: this.selectedStaff?.streetAddress2?.trim() || '',
      city: this.selectedStaff?.city?.trim() || '',
      state: this.selectedStaff?.stateName || '',
      zip: this.selectedStaff?.zip?.trim() || '',
      workPhone: this.selectedStaff?.phone || '',
      phone: this.selectedStaff?.phone || '',
      email: this.selectedStaff?.email || '',
      title: this.selectedStaff?.title || '',
    });
  }
  get fullAddress(): string {
    if (this.selectedStaff === null) {
      return '';
    }
    return [
      this.selectedStaff.streetAddress1,
      this.selectedStaff.streetAddress2 ?? '',
      this.selectedStaff.city,
      this.selectedStaff.stateName,
      this.selectedStaff.zip,
    ]
      .filter(Boolean)
      .join(', ');
  }

  setValidators(): void {
    if (this.showAddress) {
      this.selectedStaffForm
        ?.get('streetAddress1')
        ?.setValidators(Validators.required);
      this.selectedStaffForm?.get('streetAddress1')?.updateValueAndValidity();
    }

    this.selectedStaffForm
      ?.get('email')
      ?.setValidators(validateEmail(this.showEmail));
    this.selectedStaffForm?.get('email')?.updateValueAndValidity();

    if (this.showSchool) {
      this.selectedStaffForm?.get('school')?.setValidators(Validators.required);
      this.selectedStaffForm?.get('school')?.updateValueAndValidity();
    }
  }

  clearValidators(): void {
    for (const CONTROL in this.selectedStaffForm.controls) {
      this.selectedStaffForm.controls[CONTROL].setErrors(null);
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
  ngAfterViewInit(): void {
    if (this.router.url.includes('view')) {
      this.disableSearch = true;
      for (const CONTROL in this.selectedStaffForm.controls) {
        this.selectedStaffForm.controls[CONTROL].disable();
      }
    }
  }
  resetSearch(): void {
    this.isSearchVisible = false;
    this.selectedStaff = null;
    this.selectedStaffForm.get('phone')?.clearValidators();
    this.selectedStaffForm.get('phone')?.updateValueAndValidity();
    this.selectedStaffForm.get('email')?.clearValidators();
    this.selectedStaffForm.get('email')?.updateValueAndValidity();
    this.onResetSearch.next(true);
  }
}
